import CtaSection from '@/components/CtaSection'
import Icon from '@/components/Icon'
import Layout from '@/components/Layout'
import Seo from '@/components/Seo'
import { breadcrumbSchema, services, whatsappLink, whatsappMessages } from '@/lib/site-data'
import {MessageCircleDashed as MessageCircle} from 'lucide-react'

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  itemListElement: services.map((service, index) => ({
    '@type': 'Service',
    position: index + 1,
    name: service.title,
    description: service.longDescription,
    provider: {
      '@type': 'Organization',
      name: 'Muita Mídia LTDA',
    },
  })),
}

const crumbs = breadcrumbSchema([
  { name: 'Início', path: '/' },
  { name: 'Serviços', path: '/servicos' },
])

function Servicos() {
  return (
    <Layout>
      <Seo
        title="Serviços | Venda e Transferência de Páginas do Instagram | Muita Mídia"
        description="Conheça os serviços da Muita Mídia: venda de páginas nichadas do Instagram, verificação manual, transferência assistida, suporte pós-venda e consultoria de nicho."
        path="/servicos"
        jsonLd={[serviceSchema, crumbs]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0B0919]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-4xl px-6 py-16 text-center sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#C4B5FD]">
            Nossos serviços
          </span>
          <h1 className="mt-6 text-3xl font-bold leading-tight text-white sm:text-4xl">
            Tudo o que envolve comprar uma página do Instagram, do início ao pós-venda
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-400 sm:text-lg">
            Não vendemos apenas acesso a um perfil. Cuidamos da verificação, da transferência segura,
            do suporte contínuo e da orientação estratégica para você aproveitar melhor a sua compra.
          </p>
          <a
            href={whatsappLink(whatsappMessages.servicesCta)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#25D366]/20 transition-transform hover:scale-105"
          >
            <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
            Falar com um consultor
          </a>
        </div>
      </section>

      {/* LISTA DE SERVIÇOS */}
      {services.map((service, index) => {
        const isDark = index % 2 === 1
        return (
          <section key={service.slug} id={service.slug} className={isDark ? 'bg-[#0B0919]' : 'bg-white'}>
            <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-start">
                <div>
                  <div className={`inline-flex rounded-xl p-3 ${isDark ? 'bg-[#8B5CF6]/15' : 'bg-[#8B5CF6]/10'}`}>
                    <Icon name={service.icon} className={isDark ? 'h-7 w-7 text-[#C4B5FD]' : 'h-7 w-7 text-[#7C3AED]'} />
                  </div>
                  <h2 className={`mt-5 text-2xl font-bold sm:text-3xl ${isDark ? 'text-white' : 'text-zinc-900'}`}>
                    {service.title}
                  </h2>
                  <p className={`mt-3 text-base leading-relaxed ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    {service.longDescription}
                  </p>
                  <a
                    href={whatsappLink(whatsappMessages.serviceCta(service.title))}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#8B5CF6] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#7C3AED]"
                  >
                    <MessageCircle className="h-4 w-4" fill="white" strokeWidth={0} />
                    Falar sobre este serviço
                  </a>
                </div>
                <div className={`rounded-2xl border p-6 sm:p-8 ${isDark ? 'border-white/10 bg-white/[0.03]' : 'border-zinc-100 bg-zinc-50'}`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-zinc-300' : 'text-zinc-500'}`}>
                    O que inclui
                  </h3>
                  <ul className="mt-4 space-y-3">
                    {service.bullets.map(bullet => (
                      <li key={bullet} className="flex items-start gap-3">
                        <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${isDark ? 'bg-[#C4B5FD]' : 'bg-[#7C3AED]'}`} />
                        <span className={`text-sm leading-relaxed ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>
        )
      })}

      <CtaSection
        title="Ainda não sabe qual serviço faz sentido para você?"
        description="Fale com a nossa equipe pelo WhatsApp e receba uma recomendação personalizada, sem compromisso."
        message={whatsappMessages.consultingCta}
        tone="dark"
      />
    </Layout>
  )
}

export default Servicos
